import { Box, Card, Stack } from "@mui/material";
import Grid from '@mui/material/Grid';
import { IMember } from "../types/interfaces/IMember";
import ContactInformationCard from "./cards/ContactInformationCard";
import EducationAndTrainingCard from "./cards/EducationAndTrainingCard";
import MapCard from "./cards/MapCard";

/**
 * ItemCards Component
 * 
 * This component creates a holder for result item detail cards which includes contact info, education and training, and map location.
 * The display of this component is controlled by the setShow toggle in the SummaryItem component.
 * 
 * Props:
 * - member: The member object containing the member's information.
 * 
 * Example usage:
 * ```tsx
 * <ItemCards member={member} />
 * ```
 * 
 * @param {IMember} member - The member object containing the member's information.
 * @returns {JSX.Element} A component displaying the detail cards for the member.
 */
export default function ItemCards({ member }: {member : IMember}): JSX.Element {

    const hasAddress = (member: IMember): boolean => {
        return member.Addresses !== null
            && member.Addresses !== undefined
            && member.Addresses.length > 0;
    };

    const hasPhone = (member: IMember): boolean => {
        return member.Phone !== null
            && member.Phone !== undefined;
    };

    const hasEmail = (member: IMember): boolean => {
        return member.Email !== null
            && member.Email !== undefined;
    };
    
    const hasEducationTraining = (member: IMember): boolean => {
        return member.Board !== null && member.Board !== undefined
            || member.Specialties !== null && member.Specialties !== undefined
            || member.SubSpecialties !== null && member.SubSpecialties !== undefined
            || member.Interests !== null && member.Interests !== undefined
            || member.MedicalSchoolGraduateYear !== null && member.MedicalSchoolGraduateYear !== undefined
            || member.Languages !== null && member.Languages !== undefined
            || member.Gender !== null && member.Gender !== undefined
    };
    
    const showEducationTraining = hasEducationTraining(member);
    const showAddress = hasAddress(member) || hasPhone(member) || hasEmail(member);

    return (
        <Stack sx={{ width: "100%" }}>
            <Box sx={{ flexGrow: 1 }}>
                <Grid item sx={{ 
                    width: "100%", 
                    display: "flex",
                    flexDirection: 'row', 
                    flexWrap: 'wrap', 
                    justifyContent: 'flex-start', 
                    alignContent: 'flex-start',
                    }} container spacing={2}>
                    {showAddress && (
                        <Grid item sx={{ width: {xs: "100%", md: "30%", lg: "30%"} }} xs={12} md={4}>
                            <Card variant="outlined">
                                <ContactInformationCard member={member} />
                            </Card>
                        </Grid>)}
                    {showEducationTraining && (
                        <Grid item sx={{ width: {xs: "100%", md: "30%", lg: "30%"} }} xs={12} md={4}>
                            <Card variant="outlined">
                                <EducationAndTrainingCard member={member} />
                            </Card>
                        </Grid>)}
                    {showAddress && (
                        <Grid item sx={{ width: {xs: "100%", md: "30%", lg: "30%"} }} xs={12} md={4}>
                            <Card variant="outlined">
                                <MapCard member={member} />
                            </Card>
                        </Grid>)}
                </Grid>
            </Box>
        </Stack>
    );
}